import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { Loader2, Stethoscope, User } from "lucide-react";

/**
 * One-time role selection screen for users who signed up with Google WITHOUT
 * an admin-domain email. Cleared server-side once /auth/google/select-role
 * succeeds. Auto-redirects if the user shouldn't be here.
 */
export default function SeleccionarRol() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(null);

  React.useEffect(() => {
    if (user && user !== false && !user.pending_role_selection) {
      const target =
        user.role === "admin"
          ? "/admin"
          : user.role === "medico"
          ? "/medico"
          : "/dashboard";
      navigate(target, { replace: true });
    }
  }, [user, navigate]);

  const pick = async (role) => {
    setSaving(role);
    try {
      await api.post("/auth/google/select-role", { role });
      await refresh();
      toast.success("Rol configurado. Bienvenido a Siempre Salud.");
      navigate(role === "medico" ? "/medico" : "/dashboard", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-lg" data-testid="seleccion-rol-page">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-[11px] font-medium text-[#0F2942]">
              Un paso más
            </div>
            <h1 className="mt-3 font-display text-3xl font-semibold text-[#0F2942] tracking-tight">
              ¿Eres persona usuaria o especialista de salud?
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              Elige el tipo de cuenta que te corresponde. Esta configuración es única y
              define tu experiencia en Siempre Salud.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <RoleCard
              icon={<User size={26} className="text-[#2563EB]" />}
              title="Soy persona usuaria"
              copy="Quiero orientación clínica IA basada en GPC México y acceso al directorio de especialistas."
              cta="Continuar como persona usuaria"
              testid="rol-paciente-btn"
              onSelect={() => pick("paciente")}
              busy={saving === "paciente"}
              disabled={saving !== null}
            />
            <RoleCard
              icon={<Stethoscope size={26} className="text-[#2563EB]" />}
              title="Soy especialista"
              copy="Quiero aparecer en el directorio, contratar mi suscripción y recibir consultas derivadas."
              cta="Continuar como médico"
              testid="rol-medico-btn"
              onSelect={() => pick("medico")}
              busy={saving === "medico"}
              disabled={saving !== null}
            />
          </div>

          <p className="mt-5 text-[11px] text-muted-foreground text-center">
            ¿Elegiste mal? Podrás pedir el cambio a soporte una sola vez desde tu perfil.
          </p>
        </div>
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}

function RoleCard({ icon, title, copy, cta, testid, onSelect, busy, disabled }) {
  return (
    <div className="rounded-xl border border-border bg-white p-5 flex flex-col">
      <div className="w-12 h-12 rounded-lg bg-[#2563EB]/10 grid place-items-center">
        {icon}
      </div>
      <h2 className="mt-3 font-display text-lg font-semibold text-[#0F2942]">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground flex-1">{copy}</p>
      <Button
        onClick={onSelect}
        disabled={disabled}
        className="mt-4 bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
        data-testid={testid}
      >
        {busy ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Guardando…
          </>
        ) : (
          cta
        )}
      </Button>
    </div>
  );
}
